/**
 * EbmReportWorkspace — admin EBM report for one uploaded monthly export.
 *
 * A type selector switches the report:
 *   - Výkaz (P&L): each account's netto grouped by OZN base into Náklady /
 *     z toho − / Výnosy, side by side, with grand totals.
 *   - Rozvaha (balance sheet): each account's netto summed per cílová buňka
 *     (L-code) — one value per target cell.
 * Expand a line to see the účet behind it; unmapped účet are listed separately.
 * An účet marked „abs" in the zařazení is reported in Výnosy as |netto| whatever
 * its OZN says — the detail row shows it in the column it actually landed in,
 * tagged so the move is visible.
 * „Upravit" opens the rozdělení editor: money can be moved between cells by a
 * share (F1 keeps 80 %, 20 % goes to H1) or by a fixed amount read off another
 * cell (z G do B ve výši výnosů B). Touched lines carry a presun row in their
 * detail — účty + presuny always add up to the reported value.
 * Export to CSV (Excel) or open the A4 print view.
 */

import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { formatInt, formatMoney } from '@/lib/format';
import { useEbmImportsQuery, useEbmReportQuery, type EbmTyp } from './api';
import { EbmRozdeleniModal } from './EbmRozdeleniModal';
import { ebmMonthLabel, ebmPctLabel, buildReportCsv, downloadCsv } from './ebm-report-helpers';

/** What a presun row was measured by — a share, or another cell's value. */
function presunPopis(p: {
  druh: 'procento' | 'castka';
  procentoN: number;
  procentoV: number;
  odkazOzn: string;
  odkazSloupec: '' | 'N' | 'V';
}): string {
  if (p.druh === 'castka') {
    const sloupec = p.odkazSloupec === 'N' ? 'nákladů' : 'výnosů';
    return `ve výši ${sloupec} buňky ${p.odkazOzn}`;
  }
  return p.procentoN === p.procentoV
    ? ebmPctLabel(p.procentoN)
    : `${ebmPctLabel(p.procentoN)} N · ${ebmPctLabel(p.procentoV)} V`;
}

/** Marker on a detail row whose value entered the report as |netto|. */
function AbsTag({ isVykaz }: { isVykaz: boolean }) {
  return (
    <span
      className="ml-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-accent"
      title={
        isVykaz
          ? 'Vykázáno ve Výnosech jako absolutní hodnota (|netto|), bez ohledu na N/V v OZN'
          : 'Do reportu vstoupila absolutní hodnota (|netto|)'
      }
    >
      abs
    </span>
  );
}

interface EbmReportWorkspaceProps {
  /** Company admin (spravce) or platform admin — mirrors the server gate. */
  canManage: boolean;
}

const EBM_TYP_OPTIONS: { value: EbmTyp; label: string }[] = [
  { value: 'vykaz', label: 'Výkaz' },
  { value: 'rozvaha_aktiva', label: 'Rozvaha — aktiva' },
  { value: 'rozvaha_pasiva', label: 'Rozvaha — pasiva' },
];

export function EbmReportWorkspace({ canManage }: EbmReportWorkspaceProps) {
  const navigate = useNavigate();
  const [typ, setTyp] = useState<EbmTyp>('vykaz');
  const [importId, setImportId] = useState<number | undefined>(undefined);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editOpen, setEditOpen] = useState(false);

  const imports = useEbmImportsQuery(canManage, typ);
  const importsList = imports.data?.imports ?? [];

  // Default to the newest import once the list loads.
  useEffect(() => {
    if (importId == null && importsList.length > 0) setImportId(importsList[0].id);
  }, [importId, importsList]);

  const report = useEbmReportQuery(importId, typ, canManage);
  const data = report.data;
  const isVykaz = typ === 'vykaz';

  const switchTyp = (next: EbmTyp) => {
    if (next === typ) return;
    setTyp(next);
    setImportId(undefined);
    setExpanded(new Set());
  };

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="Pouze pro správce"
          title="EBM report je dostupný pouze správcům firmy."
          description="Pokud potřebujete přístup, kontaktujte správce."
        />
      </div>
    );
  }

  const rowCount = data ? (isVykaz ? data.lines.length : data.rozvaha?.lines.length ?? 0) : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Typ
          <select
            value={typ}
            onChange={(e) => switchTyp(e.target.value as EbmTyp)}
            className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none"
          >
            {EBM_TYP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Měsíc
          <select
            value={importId ?? ''}
            onChange={(e) => setImportId(e.target.value === '' ? undefined : Number(e.target.value))}
            disabled={importsList.length === 0}
            className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none"
          >
            {importsList.length === 0 && <option value="">— žádný export —</option>}
            {importsList.map((i) => (
              <option key={i.id} value={i.id}>
                {ebmMonthLabel(i.rok, i.mesic)}
                {i.nazevSouboru ? ` · ${i.nazevSouboru}` : ''}
              </option>
            ))}
          </select>
        </label>

        <Toolband.Spacer />

        {data && (
          <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            {formatInt(rowCount)} řádků ·{' '}
            {isVykaz
              ? `náklady ${formatMoney(data.totals.naklady)} · z toho záporné ${formatMoney(data.totals.nakladyZaporne)} · výnosy ${formatMoney(data.totals.vynosy)}`
              : `celkem ${formatMoney(data.rozvaha?.total ?? 0)}`}
          </span>
        )}
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon name="edit" size={12} />}
          onClick={() => setEditOpen(true)}
        >
          Upravit
        </Button>
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon name="download" size={12} />}
          disabled={!data}
          onClick={() =>
            data &&
            downloadCsv(
              `ebm-report-${typ}-${data.import.rok}-${String(data.import.mesic).padStart(2, '0')}.csv`,
              buildReportCsv(data),
            )
          }
        >
          Export CSV
        </Button>
        <Button
          variant="ink"
          size="sm"
          icon={<Icon name="external" size={12} />}
          disabled={importId == null}
          onClick={() =>
            importId != null &&
            navigate({
              to: '/management/ebm/report/tisk',
              search: { typ, importId },
              reloadDocument: true,
            })
          }
        >
          Tisknout
        </Button>
      </Toolband>

      <div
        data-testid="management-ebm-report"
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-8 pb-8 pt-5"
      >
        {imports.isLoading ? (
          <SkeletonRows />
        ) : importsList.length === 0 ? (
          <EmptyState
            eyebrow="Zatím prázdné"
            title="Žádný nahraný export."
            description="Nahrajte měsíční export tohoto typu na záložce „EBM zařazení“, poté se zde zobrazí report."
          />
        ) : report.isLoading ? (
          <SkeletonRows />
        ) : report.isError ? (
          <EmptyState
            eyebrow="Chyba"
            title="Načtení reportu selhalo."
            description={report.error instanceof Error ? report.error.message : 'Zkuste to znovu.'}
          />
        ) : data ? (
          <>
            {isVykaz ? (
              <div className="overflow-x-auto border border-line bg-paper">
                <table className="w-full border-collapse text-[12px]">
                  <thead className="bg-bg">
                    <tr className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                      <th className="w-[40px] px-3 py-2.5" />
                      <th className="px-3 py-2.5 text-left">OZN</th>
                      <th className="px-3 py-2.5 text-right">Náklady</th>
                      <th className="px-3 py-2.5 text-right">z toho −</th>
                      <th className="px-3 py-2.5 text-right">Výnosy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((l) => {
                      const open = expanded.has(l.base);
                      return (
                        <Fragment key={l.base}>
                          <tr
                            data-testid="ebm-report-line"
                            className="cursor-pointer border-t border-line hover:bg-bg"
                            onClick={() => toggle(l.base)}
                          >
                            <td className="px-3 py-2 text-ink-3">
                              <Icon name={open ? 'chevronD' : 'chevronR'} size={12} />
                            </td>
                            <td className="px-3 py-2 font-mono text-[12px] font-semibold text-ink">
                              {l.base}
                              {l.presuny.length > 0 && <SplitTag presuny={l.presuny} />}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                              {l.naklady !== 0 ? formatMoney(l.naklady) : <span className="text-ink-3">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                              {l.nakladyZaporne !== 0 ? formatMoney(l.nakladyZaporne) : <span className="text-ink-3">—</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                              {l.vynosy !== 0 ? formatMoney(l.vynosy) : <span className="text-ink-3">—</span>}
                            </td>
                          </tr>
                          {open &&
                            l.presuny.map((p, pi) => (
                              <tr key={`${l.base}-presun-${pi}`} className="border-t border-line/60 bg-bg/40">
                                <td className="px-3 py-1.5" />
                                <td className="px-3 py-1.5 text-accent">
                                  <span className="font-mono text-[11px]">
                                    {p.smer === 'out' ? `→ ${p.ozn}` : `← z ${p.ozn}`}
                                  </span>
                                  <span className="ml-1.5 text-[11px] text-ink-3">{presunPopis(p)}</span>
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-accent">
                                  {p.naklady !== 0 ? formatMoney(p.naklady) : ''}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-accent">
                                  {p.nakladyZaporne !== 0 ? formatMoney(p.nakladyZaporne) : ''}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-accent">
                                  {p.vynosy !== 0 ? formatMoney(p.vynosy) : ''}
                                </td>
                              </tr>
                            ))}
                          {open &&
                            l.ucty.map((u) => (
                              <tr key={`${l.base}-${u.ucet}`} className="border-t border-line/60 bg-bg/40">
                                <td className="px-3 py-1.5" />
                                <td className="px-3 py-1.5 text-ink-2">
                                  <span className="font-mono tabular-nums text-ink-3">{u.ucet}</span>
                                  {u.nazev ? ` · ${u.nazev}` : ''}
                                  {u.absolutni && <AbsTag isVykaz={isVykaz} />}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-ink-2">
                                  {u.side === 'N' ? formatMoney(u.netto) : ''}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-ink-2">
                                  {u.side === 'N' && u.netto < 0 ? formatMoney(u.netto) : ''}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-ink-2">
                                  {u.side === 'V' ? formatMoney(u.netto) : ''}
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-ink/30 font-semibold">
                      <td className="px-3 py-2.5" />
                      <td className="px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.06em] text-ink">Celkem</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-ink">
                        {formatMoney(data.totals.naklady)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-ink">
                        {formatMoney(data.totals.nakladyZaporne)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-ink">
                        {formatMoney(data.totals.vynosy)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="overflow-x-auto border border-line bg-paper">
                <table className="w-full border-collapse text-[12px]">
                  <thead className="bg-bg">
                    <tr className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                      <th className="w-[40px] px-3 py-2.5" />
                      <th className="px-3 py-2.5 text-left">Cílová buňka</th>
                      <th className="px-3 py-2.5 text-right">Hodnota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rozvaha?.lines ?? []).map((l) => {
                      const open = expanded.has(l.ozn);
                      return (
                        <Fragment key={l.ozn}>
                          <tr
                            data-testid="ebm-report-line"
                            className="cursor-pointer border-t border-line hover:bg-bg"
                            onClick={() => toggle(l.ozn)}
                          >
                            <td className="px-3 py-2 text-ink-3">
                              <Icon name={open ? 'chevronD' : 'chevronR'} size={12} />
                            </td>
                            <td className="px-3 py-2 font-mono text-[12px] font-semibold text-ink">
                              {l.ozn}
                              {l.presuny.length > 0 && <SplitTag presuny={l.presuny} />}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                              {formatMoney(l.hodnota)}
                            </td>
                          </tr>
                          {open &&
                            l.presuny.map((p, pi) => (
                              <tr key={`${l.ozn}-presun-${pi}`} className="border-t border-line/60 bg-bg/40">
                                <td className="px-3 py-1.5" />
                                <td className="px-3 py-1.5 text-accent">
                                  <span className="font-mono text-[11px]">
                                    {p.smer === 'out' ? `→ ${p.ozn}` : `← z ${p.ozn}`}
                                  </span>
                                  <span className="ml-1.5 text-[11px] text-ink-3">
                                    {p.druh === 'castka'
                                      ? `ve výši hodnoty buňky ${p.odkazOzn}`
                                      : ebmPctLabel(p.procento)}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-accent">
                                  {formatMoney(p.hodnota)}
                                </td>
                              </tr>
                            ))}
                          {open &&
                            l.ucty.map((u) => (
                              <tr key={`${l.ozn}-${u.ucet}`} className="border-t border-line/60 bg-bg/40">
                                <td className="px-3 py-1.5" />
                                <td className="px-3 py-1.5 text-ink-2">
                                  <span className="font-mono tabular-nums text-ink-3">{u.ucet}</span>
                                  {u.nazev ? ` · ${u.nazev}` : ''}
                                  {u.absolutni && <AbsTag isVykaz={isVykaz} />}
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono tabular-nums text-ink-2">
                                  {formatMoney(u.netto)}
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-ink/30 font-semibold">
                      <td className="px-3 py-2.5" />
                      <td className="px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.06em] text-ink">Celkem</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-ink">
                        {formatMoney(data.rozvaha?.total ?? 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {data.unmapped.length > 0 && (
              <section className="flex flex-col gap-2">
                <h4 className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-warn">
                  Nezařazené účty ({formatInt(data.unmapped.length)}) — chybí OZN, do reportu nevstupují
                </h4>
                <div className="overflow-x-auto border border-line bg-paper">
                  <table className="w-full border-collapse text-[12px]">
                    <thead className="bg-bg">
                      <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                        <th className="w-[110px] px-3 py-2.5">Účet</th>
                        <th className="px-3 py-2.5">Název</th>
                        <th className="w-[120px] px-3 py-2.5 text-right">Netto</th>
                        <th className="w-[100px] px-3 py-2.5">OZN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unmapped.map((u) => (
                        <tr key={u.ucet} className="border-t border-line">
                          <td className="px-3 py-2 font-mono tabular-nums text-ink">{u.ucet}</td>
                          <td className="px-3 py-2 text-ink-2">
                            {u.nazev || <span className="text-ink-3">— bez názvu —</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-2">
                            {formatMoney(u.netto)}
                          </td>
                          <td className="px-3 py-2 font-mono text-ink-3">{u.ozn ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        ) : null}
      </div>

      <EbmRozdeleniModal
        open={editOpen}
        onOpenChange={setEditOpen}
        typ={typ}
        cells={isVykaz ? data?.lines.map((l) => l.base) ?? [] : data?.rozvaha?.lines.map((l) => l.ozn) ?? []}
      />
    </div>
  );
}

/** „děleno" / „přijato" marker next to a cell touched by a split. */
function SplitTag({ presuny }: { presuny: { smer: 'out' | 'in' }[] }) {
  const out = presuny.some((p) => p.smer === 'out');
  const inbound = presuny.some((p) => p.smer === 'in');
  const label = out && inbound ? 'děleno · přijato' : out ? 'děleno' : 'přijato';
  return (
    <span
      className="ml-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-accent"
      title="Řádek je dotčen rozdělením buněk — rozbalte pro detail"
    >
      {label}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}
